/**
 * Table showing all price points with verifiable source links
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import type { ModelSummary } from './types'

interface SourceTableProps {
  summaries: ModelSummary[]
  priceType: 'input' | 'output'
}

export const SourceTable: React.FC<SourceTableProps> = ({
  summaries,
  priceType,
}) => {
  const { t } = useTranslation('aiCostTrends')
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<'price' | 'date' | 'provider'>('price')

  const toggleExpanded = (modelId: string) => {
    const next = new Set(expandedModels)
    if (next.has(modelId)) {
      next.delete(modelId)
    } else {
      next.add(modelId)
    }
    setExpandedModels(next)
  }

  // Sort summaries
  const sortedSummaries = [...summaries].sort((a, b) => {
    switch (sortBy) {
      case 'price':
        return priceType === 'input'
          ? a.currentInputPer1M - b.currentInputPer1M
          : a.currentOutputPer1M - b.currentOutputPer1M
      case 'date':
        const aDate = a.dataPoints[a.dataPoints.length - 1]?.timestamp || 0
        const bDate = b.dataPoints[b.dataPoints.length - 1]?.timestamp || 0
        return bDate - aDate
      case 'provider':
        return a.providerName.localeCompare(b.providerName)
      default:
        return 0
    }
  })

  const formatPrice = (price: number) => {
    if (price >= 1) return `$${price.toFixed(2)}`
    if (price >= 0.1) return `$${price.toFixed(2)}`
    return `$${price.toFixed(3)}`
  }


  const sortOptions = ['price', 'date', 'provider'] as const
  const sortLabels: Record<typeof sortOptions[number], string> = {
    price: t('table.price'),
    date: t('table.date'),
    provider: t('table.provider'),
  }

  return (
    <div className="overflow-x-auto">
      {/* Sort controls */}
      <div className="flex gap-2 mb-4 text-sm">
        <span className="text-secondary">{t('table.sortBy')}</span>
        {sortOptions.map((option) => (
          <button
            key={option}
            onClick={() => setSortBy(option)}
            className={`px-2 py-0.5 rounded ${
              sortBy === option
                ? 'bg-accent text-white'
                : 'text-secondary hover:text-primary'
            }`}
          >
            {sortLabels[option]}
          </button>
        ))}
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-secondary text-left">
            <th className="py-2 px-3 text-secondary font-medium">{t('table.provider')}</th>
            <th className="py-2 px-3 text-secondary font-medium">{t('table.model')}</th>
            <th className="py-2 px-3 text-secondary font-medium text-right">
              {priceType === 'input' ? t('table.currentInput') : t('table.currentOutput')}
            </th>
            <th className="py-2 px-3 text-secondary font-medium text-right">
              {t('table.change')}
            </th>
            <th className="py-2 px-3 text-secondary font-medium">
              {t('table.latestSource')}
            </th>
            <th className="py-2 px-3 text-secondary font-medium w-10"></th>
          </tr>
        </thead>
        <tbody>
          {sortedSummaries.map((summary) => {
            const isExpanded = expandedModels.has(summary.modelId)
            const currentPoint = summary.dataPoints[summary.dataPoints.length - 1]
            const currentPrice =
              priceType === 'input'
                ? summary.currentInputPer1M
                : summary.currentOutputPer1M
            const hasHistory = summary.dataPoints.length > 1

            return (
              <React.Fragment key={summary.modelId}>
                <tr
                  className={`border-b border-secondary/30 hover:bg-tertiary/30 ${
                    hasHistory ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => hasHistory && toggleExpanded(summary.modelId)}
                >
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: summary.providerColor }}
                      />
                      <span className="text-primary">{summary.providerName}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-primary font-medium">
                    {summary.modelName}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-primary">
                    {formatPrice(currentPrice)}/1M
                  </td>
                  <td className="py-2 px-3 text-right">
                    {summary.priceReduction > 0 ? (
                      <span className="text-green-600 font-medium">
                        -{summary.priceReduction.toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-secondary">-</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {currentPoint && (
                      <a
                        href={currentPoint.source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-accent hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="truncate max-w-[200px]">
                          {currentPoint.source.title || t('table.source')}
                        </span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {hasHistory && (
                      <span className="text-secondary">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </span>
                    )}
                  </td>
                </tr>

                {/* Expanded history */}
                {isExpanded &&
                  summary.dataPoints.slice(0, -1).reverse().map((point, i) => (
                    <tr
                      key={`${summary.modelId}-${i}`}
                      className="bg-tertiary/20 border-b border-secondary/20"
                    >
                      <td className="py-1.5 px-3 pl-8 text-secondary text-xs">
                        {new Date(point.date).toLocaleDateString()}
                      </td>
                      <td className="py-1.5 px-3 text-secondary text-xs">
                        {point.notes || t('table.priceUpdate')}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-secondary text-xs">
                        {formatPrice(
                          priceType === 'input'
                            ? point.inputPer1M
                            : point.outputPer1M
                        )}
                        /1M
                      </td>
                      <td className="py-1.5 px-3"></td>
                      <td className="py-1.5 px-3">
                        <a
                          href={point.source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-accent hover:underline text-xs"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td></td>
                    </tr>
                  ))}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default SourceTable
